import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { MapPin, Trash2, Plus, AlertCircle, CheckCircle2, Pencil, X } from "lucide-react";

const EMPTY = {
  name: "",
  zonal_head_name: "",
  designation: "",
  contact_email: "",
  contact_phone: "",
  states_covered: "",
  description: "",
  priority: 0,
};

export default function ZonesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = () => api.get("/zones").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setLoading(true);
    try {
      const payload = { ...form, priority: Number(form.priority) || 0 };
      if (editingId) {
        await api.patch(`/zones/${editingId}`, payload);
        setOk("Zone updated.");
      } else {
        await api.post("/zones", payload);
        setOk("Zone added. It is now visible on the public /zones page.");
      }
      resetForm();
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const edit = (z) => {
    setEditingId(z.id);
    setForm({
      name: z.name || "",
      zonal_head_name: z.zonal_head_name || "",
      designation: z.designation || "",
      contact_email: z.contact_email || "",
      contact_phone: z.contact_phone || "",
      states_covered: z.states_covered || "",
      description: z.description || "",
      priority: z.priority || 0,
    });
    setOk(""); setErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this zone?")) return;
    try { await api.delete(`/zones/${id}`); refresh(); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="zones-manager">
      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-nkf-red" />
        <h2 className="font-display text-xl text-nkf-blue">Federation Zones & Zonal Heads</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Manage regional zones of NKF Bharat and their appointed zonal heads. Published on the public page <span className="font-semibold">/zones</span>.
      </p>

      <form onSubmit={submit} className="mt-5 grid grid-cols-1 md:grid-cols-12 gap-3">
        <Input wide="md:col-span-5" label="Zone Name *" value={form.name} set={(v) => set("name", v)} required placeholder="e.g. North Zone" testid="zone-name" />
        <Input wide="md:col-span-5" label="Zonal Head Name" value={form.zonal_head_name} set={(v) => set("zonal_head_name", v)} placeholder="Full name" testid="zone-head" />
        <Input wide="md:col-span-2" label="Priority" value={form.priority} set={(v) => set("priority", v)} placeholder="0" testid="zone-priority" />

        <Input wide="md:col-span-4" label="Designation" value={form.designation} set={(v) => set("designation", v)} placeholder="e.g. Chairperson" testid="zone-designation" />
        <Input wide="md:col-span-4" label="Contact Email" value={form.contact_email} set={(v) => set("contact_email", v)} placeholder="head.zone@nkfbharat.org" testid="zone-email" />
        <Input wide="md:col-span-4" label="Contact Phone" value={form.contact_phone} set={(v) => set("contact_phone", v)} placeholder="+91 " testid="zone-phone" />

        <Input wide="md:col-span-12" label="States / Regions Covered" value={form.states_covered} set={(v) => set("states_covered", v)} placeholder="e.g. Delhi, Haryana, Punjab, J&K" testid="zone-states" />
        <label className="block md:col-span-12" data-testid="zone-description">
          <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Description</span>
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Short note about the zone's coverage or focus"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
        </label>

        <div className="md:col-span-12 flex justify-end gap-2">
          {editingId && (
            <button type="button" onClick={resetForm} data-testid="zone-cancel-edit"
              className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 text-sm font-bold uppercase tracking-widest px-4 py-2.5 rounded hover:bg-slate-50 transition">
              <X className="h-4 w-4" /> Cancel
            </button>
          )}
          <button disabled={loading} data-testid="zone-submit"
            className="inline-flex items-center gap-1 bg-nkf-blue text-white text-sm font-bold uppercase tracking-widest px-5 py-2.5 rounded hover:bg-nkf-navy transition disabled:opacity-60">
            <Plus className="h-4 w-4" /> {loading ? "Saving…" : (editingId ? "Update Zone" : "Add Zone")}
          </button>
        </div>
      </form>

      {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
      {ok && <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 p-2.5 rounded text-sm"><CheckCircle2 className="h-4 w-4 mt-0.5" /> {ok}</div>}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((z) => (
          <div key={z.id} data-testid={`zone-row-${z.id}`} className="border border-slate-200 rounded-md p-4 flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display text-lg text-nkf-blue truncate">{z.name}</div>
                <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold mt-0.5">
                  {z.zonal_head_name || "Head not assigned"}
                  {z.designation ? ` · ${z.designation}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => edit(z)} data-testid={`zone-edit-${z.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(z.id)} data-testid={`zone-del-${z.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Remove">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            {(z.states_covered || z.description) && (
              <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                {z.states_covered && <div><span className="font-semibold text-slate-700">States:</span> {z.states_covered}</div>}
                {z.description && <div className="mt-1">{z.description}</div>}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-slate-500">
              {z.contact_email && <span>📧 {z.contact_email}</span>}
              {z.contact_phone && <span>📞 {z.contact_phone}</span>}
            </div>
          </div>
        ))}
        {!items.length && <div className="md:col-span-2 text-center text-slate-400 text-sm py-6 border border-dashed rounded-md">No zones added yet.</div>}
      </div>
    </section>
  );
}

function Input({ wide, label, value, set, required, placeholder, testid }) {
  return (
    <label className={`block ${wide || ""}`} data-testid={testid}>
      <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">{label}</span>
      <input required={required} placeholder={placeholder} value={value} onChange={(e) => set(e.target.value)}
        className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
    </label>
  );
}
